/**
 * Calibration Data Parser
 * 
 * Parses calibration flags from API responses and provides validation
 * and fallback mechanisms for data source availability.
 */

import { ICalibrationAvailability } from '../types/calibration';

/**
 * Default calibration state (fallback to both-true for common metrics only)
 */
const DEFAULT_CALIBRATION: ICalibrationAvailability = {
  is_mscience_daily: false,
  is_vgi_daily: false,
};

/**
 * Parse calibration flags from API response
 * 
 * Extracts is_mscience_daily and is_vgi_daily flags from the API response
 * and converts them from string format ("true"/"false") to boolean.
 * 
 * @param apiResponse - Raw API response object containing calibration flags
 * @returns Parsed calibration availability with boolean flags
 * 
 * @example
 * ```typescript
 * const response = {
 *   is_mscience_daily: "true",
 *   is_vgi_daily: "false"
 * };
 * const flags = parseCalibrationFlags(response);
 * // Returns: { is_mscience_daily: true, is_vgi_daily: false }
 * ```
 */
export function parseCalibrationFlags(apiResponse: any): ICalibrationAvailability {
  try {
    // Extract flags from response
    const mscienceFlag = apiResponse?.is_mscience_daily;
    const vgiFlag = apiResponse?.is_vgi_daily;

    // Check if flags are missing
    if (mscienceFlag === undefined && vgiFlag === undefined) {
      console.warn(
        '[CalibrationParser] Calibration flags missing from API response. Using default (both-true: common metrics only).'
      );
      return DEFAULT_CALIBRATION;
    }

    // Convert string flags to boolean
    const is_mscience_daily = convertStringToBoolean(mscienceFlag);
    const is_vgi_daily = convertStringToBoolean(vgiFlag);

    // Validate the parsed data
    const calibrationData: ICalibrationAvailability = {
      is_mscience_daily,
      is_vgi_daily,
    };

    if (!validateCalibrationData(calibrationData)) {
      console.warn(
        '[CalibrationParser] Invalid calibration data. Using default (both-true: common metrics only).'
      );
      return DEFAULT_CALIBRATION;
    }

    return calibrationData;
  } catch (error) {
    console.error('[CalibrationParser] Error parsing calibration flags:', error);
    console.warn('[CalibrationParser] Falling back to default (both-true: common metrics only).');
    return DEFAULT_CALIBRATION;
  }
}

/**
 * Convert string flag to boolean
 * 
 * Handles conversion of string "true"/"false" to boolean values.
 * Also handles boolean values passed directly.
 * 
 * @param value - String or boolean value to convert
 * @returns Boolean value
 */
function convertStringToBoolean(value: any): boolean {
  // If already boolean, return as-is
  if (typeof value === 'boolean') {
    return value;
  }

  // If string, convert
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase().trim();
    if (lowerValue === 'true') return true;
    if (lowerValue === 'false') return false;
  }

  // Default to false for any other value
  console.warn(`[CalibrationParser] Unexpected flag value: ${value}. Defaulting to false.`);
  return false;
}

/**
 * Validate calibration data integrity
 * 
 * Ensures the calibration data has the correct structure and valid values.
 * 
 * @param data - Calibration data to validate
 * @returns True if data is valid, false otherwise
 */
export function validateCalibrationData(data: ICalibrationAvailability): boolean {
  // Check if data exists
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check if required fields exist and are boolean
  if (typeof data.is_mscience_daily !== 'boolean') {
    return false;
  }

  if (typeof data.is_vgi_daily !== 'boolean') {
    return false;
  }

  // At least one source should be available (edge case check)
  // If both are false, something might be wrong, but we'll allow it
  // and let the fallback logic handle it
  return true;
}

/**
 * Get cached calibration data
 * 
 * Currently returns null as caching is handled at component level.
 * This function is provided for future caching implementation.
 * 
 * @returns Cached calibration data or null
 */
export function getCachedCalibrationData(): ICalibrationAvailability | null {
  // Caching is currently handled at component state level
  // This function is a placeholder for future implementation
  return null;
}

/**
 * Determine data source scenario from calibration flags
 * 
 * Helper function to determine which data source scenario applies
 * based on the calibration flags.
 * 
 * @param calibrationData - Calibration availability flags
 * @returns Data source scenario: 'mscience', 'vgi', 'both', or 'none'
 */
export function getDataSourceScenario(
  calibrationData: ICalibrationAvailability
): 'mscience' | 'vgi' | 'both' | 'none' {
  const { is_mscience_daily, is_vgi_daily } = calibrationData;

  if (is_mscience_daily && is_vgi_daily) {
    return 'both';
  }

  if (is_mscience_daily && !is_vgi_daily) {
    return 'mscience';
  }

  if (!is_mscience_daily && is_vgi_daily) {
    return 'vgi';
  }

  // Neither source available - return 'none' to be handled specially
  console.warn('[CalibrationParser] No data sources available.');
  return 'none';
}
